import { notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import last from "lodash/last";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { formatInTimeZone } from "shared/lib/utils";

import { hasPostiesQuiz } from "scmp-app/components/article/article-render/posties/helpers";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { postiesTodaysReadContentItemContent$key } from "scmp-app/queries/__generated__/postiesTodaysReadContentItemContent.graphql";

import {
  Container,
  Content,
  FlagContainer,
  FlagText,
  Headline,
  ImageWrapper,
  Section,
  StyledButton,
  StyledContentCoverImage,
  StyledEntityLink,
  StyledFlag,
  StyledImageEntityLink,
  StyledOffsetContainer,
  StyledQuizFlag,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: postiesTodaysReadContentItemContent$key;
};

export const ContentItemPostiesTodaysRead = forwardRef<HTMLDivElement, Props>(
  ({ className, customQueryParameters, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment postiesTodaysReadContentItemContent on Content {
          sections {
            value {
              name
              urlAlias
            }
          }
          ...entityLink
          ...contentCoverImageContent @arguments(withSize1200x800: true, withSize540x360: true)
          ...hooksContentItemProvidersContent @arguments(withHeadline: true)
          ... on Article {
            ...helpersHasPostiesQuiz
          }
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(content);

    const section = last(first(content?.sections)?.value);
    const day = formatInTimeZone(new Date(), "d");
    const month = formatInTimeZone(new Date(), "MMM");
    const hasQuiz = hasPostiesQuiz(content);

    return (
      <Container className={className} ref={reference}>
        <ImageWrapper>
          <StyledImageEntityLink query={customQueryParameters} reference={content}>
            <StyledOffsetContainer borderRadius="20px">
              <StyledContentCoverImage
                reference={content}
                responsiveVariants={{
                  desktopUp: "size1200x800",
                  mobileUp: "size540x360",
                  tabletUp: "size540x360",
                }}
              />
              <FlagContainer>
                <StyledFlag />
                <FlagText>
                  {month}
                  <span>{day}</span>
                </FlagText>
              </FlagContainer>
              {hasQuiz && <StyledQuizFlag />}
            </StyledOffsetContainer>
          </StyledImageEntityLink>
        </ImageWrapper>

        <Content>
          {notEmpty(section) && <Section pathname={section.urlAlias}>{section?.name}</Section>}
          <StyledEntityLink query={customQueryParameters} reference={content}>
            <Headline>
              {providers.headline({
                hideTag: true,
                preferSocialHeadline: true,
              })}
            </Headline>
            <StyledButton variant="button">READ MORE</StyledButton>
          </StyledEntityLink>
        </Content>
      </Container>
    );
  },
);

ContentItemPostiesTodaysRead.displayName = "ContentItemPostiesTodaysRead";
